/**
 * CITIES DATA — 26 cities (14 India + 12 International)
 * Powers /locations/:slug pages with unique localized 600-800 word content.
 */

const HERO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/wssipa5q_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg";
const HERO_MUMBAI =
  "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/fnvfaxzf_IMG_3305.JPG.jpeg";

export const CITIES = [
  // ============ INDIA ============
  {
    slug: "mumbai",
    city: "Mumbai",
    country: "IN",
    region: "Maharashtra",
    type: "india",
    metaTitle:
      "Wedding Invitations Mumbai | Digital E-Invites in Mumbai | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations in Mumbai — custom designed, WhatsApp delivered, ready in 24 hours. Trusted by couples across Bandra, Worli, Juhu & beyond.",
    heroImage: HERO_MUMBAI,
    intro:
      "Mumbai weddings are a category of their own — equal parts cinematic, cosmopolitan and deeply rooted in tradition. From sea-facing sangeets at the Taj Mahal Palace to mehndi mornings at private bungalows in Bandra, from Marathi traditions in Worli to multicultural inter-state celebrations in Powai, this is the city where every wedding tells a different story. We design wedding invitations in Mumbai that honour that range — bespoke, cinematic, and unmistakably yours.",
    sections: [
      {
        h2: "Wedding invitations designed for Mumbai weddings",
        paragraphs: [
          "Mumbai's wedding scene moves at the pace of the city itself — fast, ambitious and design-conscious. Couples here want their invitations to match the production value of their wedding films, the styling of their pre-wedding shoots and the curation of their venues. We design Mumbai wedding invitations with that standard in mind — cinematic motion, hand-set typography, custom illustration of the Gateway, Marine Drive, Bandra-Worli Sea Link or your venue's most loved architectural detail.",
          "Whether your wedding is at the Taj Mahal Palace, ITC Maratha, Trident Nariman Point, Sofitel BKC, Renaissance Powai, JW Marriott Juhu, or a private heritage bungalow in Bandra or Khar — our invitations are designed to feel as considered as your venue.",
        ],
        bullets: [
          "Cinematic invite films featuring Mumbai's coastline & skyline",
          "Custom illustration of Gateway, Sea Link or your venue",
          "Marathi + English bilingual typesetting",
          "WhatsApp delivery to your full guest list across Mumbai & abroad",
          "Print-ready files for physical Mumbai distribution",
        ],
      },
      {
        h2: "Mumbai neighbourhoods we serve",
        paragraphs: [
          "We design wedding invitations for couples across every Mumbai neighbourhood — South Mumbai (Colaba, Marine Drive, Nariman Point, Worli, Lower Parel), Bandra-Khar, Juhu-Vile Parle, Andheri-Versova, Powai-Vikhroli, BKC, Thane and Navi Mumbai. The entire process happens on WhatsApp, so distance across the city is never a constraint.",
        ],
      },
      {
        h2: "Why Mumbai couples choose InvitationWala",
        paragraphs: [
          "Mumbai couples are design-literate — they recognise the difference between a template and a true commission. They also move fast. Our design team works on Mumbai time, with WhatsApp brief calls available between 9 AM and 11 PM IST, same-day expedite delivery for last-minute requirements, and a portfolio that includes work for Mumbai-based founders, creators, lawyers, doctors and second-generation business families.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design wedding invitations for Mumbai weddings specifically?",
        a: "Yes — many of our most loved wedding invitations have been for Mumbai weddings. We can include Mumbai-specific illustrations (Gateway of India, Sea Link, Marine Drive, your venue) and design in Marathi-English bilingual format if needed.",
      },
      {
        q: "How do I order a wedding invitation in Mumbai?",
        a: "Simply WhatsApp us your wedding date, function list and reference images. We share a transparent quote within an hour, and the entire design process happens on WhatsApp — no studio visits required.",
      },
      {
        q: "Can the invite mention Mumbai venues like the Taj or Trident?",
        a: "Absolutely — we typeset venue names with correct formatting and can include venue illustrations if you'd like. Common Mumbai venues we've designed for include the Taj Mahal Palace, ITC Maratha, JW Marriott Juhu, Sofitel BKC and Renaissance Powai.",
      },
      {
        q: "Do you ship printed wedding stationery in Mumbai?",
        a: "Yes — our sister brand Stationery Wala ships premium printed wedding stationery (welcome signs, itinerary cards, menus, favours) to every Mumbai pincode via Bluedart Express. Delivery is typically 3-4 business days within Mumbai.",
      },
      {
        q: "How fast can a Mumbai wedding invite be delivered?",
        a: "Most Mumbai wedding invitations are delivered within 24 hours from final brief. Same-day delivery is available for save-the-dates and single-function invites.",
      },
      {
        q: "Do you design Marathi wedding invitations?",
        a: "Yes — we typeset Marathi-language wedding invitations in Devanagari with traditional Marathi script conventions. Bilingual Marathi-English invitations are one of our most requested Mumbai formats.",
      },
    ],
  },
  {
    slug: "delhi",
    city: "Delhi",
    country: "IN",
    region: "NCR",
    type: "india",
    metaTitle:
      "Wedding Invitations Delhi NCR | Digital E-Invites in Delhi | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations in Delhi NCR — custom designed, WhatsApp delivered, 24-hour turnaround. Trusted by couples across South Delhi, Gurgaon, Noida.",
    heroImage: HERO_DEFAULT,
    intro:
      "Delhi weddings are grand by tradition and elegant by choice. From farmhouse celebrations in Chhattarpur and Mehrauli, to luxury hotel weddings at the Leela, the Imperial, the Oberoi and ITC Maurya, to destination-style celebrations within the city itself — Delhi sets the bar for ambitious Indian weddings. We design wedding invitations in Delhi that match that ambition with craft, restraint and cinematic motion.",
    sections: [
      {
        h2: "Wedding invitations designed for Delhi NCR",
        paragraphs: [
          "Delhi weddings often span four to six functions, multiple venues, and guests travelling from across India and the diaspora. Our wedding invitations are designed to carry that complexity gracefully — each function gets its own card with venue, timing and dress code, while one cohesive design language runs through the entire suite.",
          "Whether your wedding is at The Leela Palace Chanakyapuri, ITC Maurya, The Imperial, The Oberoi New Delhi, Hyatt Regency, Roseate House, The Lalit, JW Marriott Aerocity, Westin Gurgaon, or a private farmhouse in Chhattarpur — our invitations honour the venue and the family.",
        ],
        bullets: [
          "Multi-function suites for 4-6 day Delhi weddings",
          "Custom illustration of farmhouse, palace or hotel venue",
          "Hindi + English bilingual typesetting",
          "WhatsApp delivery across Delhi, Gurgaon, Noida, Faridabad",
          "Punjabi script for couples honouring Punjabi heritage",
        ],
      },
      {
        h2: "Delhi NCR areas we serve",
        paragraphs: [
          "We design invitations for couples across all of Delhi NCR — Lutyens Delhi, South Delhi (Vasant Vihar, Defence Colony, GK, Saket), Gurgaon (Golf Course Road, Cyber City, MG Road), Noida, Greater Noida, Faridabad, Ghaziabad and Dwarka. The process happens entirely on WhatsApp — no studio visits across NCR traffic.",
        ],
      },
      {
        h2: "Why Delhi couples choose us",
        paragraphs: [
          "Delhi couples value heritage, hospitality and high production value. Our wedding invitations bring all three — heritage in our type choices and motif vocabulary, hospitality in our WhatsApp-led process, and production value in our cinematic motion. Many of our most loved Delhi clients are second-generation business families, lawyers, designers, restaurateurs and NRI couples returning home to marry.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design wedding invitations for Delhi NCR couples?",
        a: "Yes — Delhi NCR is one of our most active markets. We design invitations for weddings across Delhi, Gurgaon, Noida and Faridabad with options for Hindi, Punjabi, Urdu or English-only typography.",
      },
      {
        q: "Can the invite mention Delhi venues like ITC Maurya or The Imperial?",
        a: "Absolutely — we typeset venue names correctly and can include custom illustrations of your venue's facade or interior architecture if you'd like.",
      },
      {
        q: "Do you ship printed wedding stationery to Delhi?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery via Bluedart Express to every Delhi, Gurgaon and Noida pincode. Delivery is typically 3-4 business days.",
      },
      {
        q: "Can the invite be in Punjabi or Urdu?",
        a: "Yes — Delhi has a strong Punjabi and Urdu-speaking wedding tradition. We typeset wedding invitations in Gurmukhi and Nastaliq scripts with traditional conventions.",
      },
      {
        q: "How fast is delivery for a Delhi wedding invitation?",
        a: "Standard delivery is 24 hours from final brief. Same-day expedite is available for urgent dates.",
      },
      {
        q: "Do you design Sikh wedding invitations in Delhi?",
        a: "Yes — many of our Delhi clients have Sikh Anand Karaj ceremonies. We design Sikh wedding invitations with Ek Onkar symbol, Gurmukhi script and traditional Sikh aesthetic.",
      },
    ],
  },
  {
    slug: "bangalore",
    city: "Bangalore",
    country: "IN",
    region: "Karnataka",
    type: "india",
    metaTitle:
      "Wedding Invitations Bangalore | Digital E-Invites in Bangalore | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations in Bangalore — custom designed, WhatsApp delivered. Kannada/Tamil/Telugu bilingual options. Ready in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Bangalore weddings sit at the crossroads of South Indian tradition and tech-city modernity. From traditional Iyer, Iyengar and Vokkaliga ceremonies to modern destination-style weddings at vineyards and lakeside villas, the city's weddings are as diverse as its people. We design wedding invitations in Bangalore for couples across every community — Tamil Brahmin, Kannada, Telugu, Malayali, Coorgi, North Indian transplants, and international NRI families returning to marry.",
    sections: [
      {
        h2: "Bangalore weddings we design for",
        paragraphs: [
          "Bangalore's wedding scene spans traditional kalyana mantapams in Malleshwaram and Jayanagar to contemporary venues like the Leela Palace, ITC Royal Gardenia, Taj West End, JW Marriott Whitefield, Shangri-La and vineyard estates outside the city. Our wedding invitations are designed to feel right whether your wedding leans deeply traditional or modern destination-style.",
        ],
        bullets: [
          "Custom illustration of Vidhana Soudha, Lalbagh or your venue",
          "Kannada / Tamil / Telugu / Malayalam bilingual typesetting",
          "South Indian temple-traditional aesthetic option",
          "Modern minimal aesthetic for tech-city couples",
          "WhatsApp delivery across Bangalore, ORR, Whitefield, Electronic City",
        ],
      },
      {
        h2: "Bangalore areas we serve",
        paragraphs: [
          "We design invitations for couples across every Bangalore neighbourhood — Indiranagar, Koramangala, HSR Layout, Whitefield, Electronic City, JP Nagar, Jayanagar, Malleshwaram, Sadashivanagar, Hebbal and the ORR tech corridor. NRI couples returning to Bangalore (especially from the US tech industry) are a particularly loved client segment.",
        ],
      },
      {
        h2: "Why Bangalore couples choose us",
        paragraphs: [
          "Bangalore couples are often design-literate and detail-oriented — they ask thoughtful questions about typography, palette and motion. Our team enjoys that scrutiny. Many of our most loved Bangalore clients are tech founders, product designers, doctors, lawyers and academics for whom craft and precision matter.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design South Indian wedding invitations?",
        a: "Yes — we typeset wedding invitations in Kannada, Tamil, Telugu and Malayalam with traditional script conventions. We can include South Indian temple motifs, kolam patterns, banana leaf borders and Dravidian architectural illustrations.",
      },
      {
        q: "Can you design for Tamil Brahmin or Iyengar weddings in Bangalore?",
        a: "Absolutely — we have designed many Tamil Iyer and Iyengar wedding invitations in Bangalore, with traditional Tamil script, vedic motifs and sect-specific typography conventions.",
      },
      {
        q: "How do I order from Bangalore?",
        a: "WhatsApp us your wedding date, functions and references. We respond within minutes with a transparent quote. The entire design process happens on WhatsApp.",
      },
      {
        q: "Do you ship printed wedding stationery in Bangalore?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Bangalore pincode via Bluedart Express. Delivery is typically 3-4 business days.",
      },
      {
        q: "Can the invite mention Bangalore venues?",
        a: "Yes — we typeset venue names with correct formatting and can include venue illustrations of the Leela Palace, ITC Royal Gardenia, Taj West End or your chosen mantapam.",
      },
      {
        q: "Do you design Konkani or Coorgi wedding invitations?",
        a: "Yes — for Konkani, Coorgi and other regional communities, we adapt the typography and design language to honour community-specific traditions.",
      },
    ],
  },
  {
    slug: "hyderabad",
    city: "Hyderabad",
    country: "IN",
    region: "Telangana",
    type: "india",
    metaTitle:
      "Wedding Invitations Hyderabad | Nizami E-Invites | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations in Hyderabad — Nizami heritage aesthetics, Urdu calligraphy, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Hyderabad weddings carry the romance of nizami heritage — Persian calligraphy, gold-leaf detailing, deccan blue palettes and the architectural grace of Charminar, Falaknuma and Chowmahalla Palace. At InvitationWala we design wedding invitations in Hyderabad that honour this heritage while feeling completely current — Urdu typography that breathes, gold-foil motion that shimmers, and palettes that draw from the city's own architectural genius.",
    sections: [
      {
        h2: "Hyderabadi wedding aesthetic in design",
        paragraphs: [
          "Hyderabadi weddings are uniquely beautiful — the city's history as a Mughal-influenced princely state shows up in nikah ceremonies, mehndi traditions and culinary rituals like the post-wedding qubani-ka-meetha. Our wedding invitations honour this heritage with Urdu nastaliq calligraphy, Persian floral motifs, deccan-blue and gold palettes, and architectural references to Hyderabad's iconic skyline.",
        ],
        bullets: [
          "Urdu Nastaliq calligraphy with traditional ligatures",
          "Custom illustration of Charminar, Falaknuma or your venue",
          "Bismillah and Islamic-traditional design language",
          "Telugu typography for Telugu-speaking families",
          "WhatsApp delivery across Hyderabad, Secunderabad, Hitech City",
        ],
      },
      {
        h2: "Hyderabad areas and venues we serve",
        paragraphs: [
          "We design invitations for couples across Hyderabad, Secunderabad, Hitech City, Gachibowli, Madhapur, Banjara Hills, Jubilee Hills and Old City. Our portfolio includes weddings at Taj Falaknuma Palace, ITC Kohenur, Park Hyatt, Westin Mindspace, Trident, Marriott Convention Centre, and traditional venues in the Old City.",
        ],
      },
      {
        h2: "Why Hyderabad couples choose us",
        paragraphs: [
          "Hyderabad has a deeply design-aware wedding culture — couples here recognise the difference between authentic Urdu typography and a Photoshop transliteration. Our calligraphers and typesetters work in proper Nastaliq, with correct ligature behaviour and traditional invocation forms. This care is what brings Hyderabadi families back to us, and why so many of our wedding clients arrive through word-of-mouth referrals.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Muslim wedding invitations in Hyderabad?",
        a: "Yes — Hyderabadi Muslim weddings are a significant part of our portfolio. We typeset in proper Urdu Nastaliq, include Bismillah and Islamic-traditional motifs, and follow correct invitation hierarchy conventions.",
      },
      {
        q: "Can the invite include Charminar or Falaknuma illustrations?",
        a: "Absolutely — we draw custom city silhouettes and venue illustrations as part of our standard luxury and premium services.",
      },
      {
        q: "Do you typeset in Telugu and Urdu simultaneously?",
        a: "Yes — bilingual Telugu-Urdu or Telugu-English-Urdu invitations are common for Hyderabad weddings, and we typeset all three scripts together.",
      },
      {
        q: "How do I order a Hyderabad wedding invitation?",
        a: "WhatsApp us your wedding date, function list and references. We respond within minutes with a transparent quote and complete the entire design process on WhatsApp.",
      },
      {
        q: "Do you ship printed stationery to Hyderabad?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Hyderabad and Secunderabad pincode via Bluedart Express.",
      },
      {
        q: "Can you design for nikah and walima invitations separately?",
        a: "Yes — many Hyderabadi weddings have separate nikah and walima invitations. We design both as part of one cohesive suite or as two distinct invitations as you prefer.",
      },
    ],
  },
  {
    slug: "chennai",
    city: "Chennai",
    country: "IN",
    region: "Tamil Nadu",
    type: "india",
    metaTitle:
      "Wedding Invitations Chennai | Tamil Wedding E-Invites | InvitationWala",
    metaDescription:
      "Premium wedding invitations in Chennai — Tamil typography, temple aesthetics, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Chennai weddings are anchored in traditions that go back centuries — vedic ceremonies, kalyana mantapams, intricate kolam thresholds and the deep symbolism of Tamil Brahmin, Iyengar and Mudaliar traditions. At InvitationWala we design wedding invitations in Chennai with respect for that heritage — Tamil typography set correctly, temple architecture referenced precisely, and the design restraint that Chennai weddings have always valued.",
    sections: [
      {
        h2: "Chennai wedding aesthetics we design",
        paragraphs: [
          "From traditional Iyer and Iyengar weddings with vedic ceremonies, to Mudaliar and Chettiar weddings with their grand mantapam settings, to modern Chennai couples blending tradition with contemporary motion — we design every Chennai wedding invitation with appropriate cultural sensitivity. Our Tamil typography is set in proper Tamil script (not transliterated English), with correct conjunct behaviour and traditional ceremonial invocations.",
        ],
        bullets: [
          "Tamil typography in proper Tamil script (no transliteration)",
          "Temple-gopuram architectural illustrations",
          "Kolam-pattern decorative borders",
          "Banana-leaf, mango-leaf, kalasam ceremonial motifs",
          "WhatsApp delivery across Chennai, OMR, ECR, suburbs",
        ],
      },
      {
        h2: "Chennai areas and venues we serve",
        paragraphs: [
          "We design invitations for couples across T. Nagar, Adyar, Besant Nagar, Mylapore, Anna Nagar, Velachery, ECR, OMR, and the wider Chennai region. Common venues include ITC Grand Chola, Taj Coromandel, Hyatt Regency, Leela Palace, traditional kalyana mantapams in Mylapore and T. Nagar, and beach resorts along ECR.",
        ],
      },
      {
        h2: "Why Chennai couples choose us",
        paragraphs: [
          "Chennai is one of the most discerning Indian wedding markets — couples here value heritage, restraint and craft over showmanship. Our wedding invitations align naturally with that sensibility. We have designed for academic families, classical musicians, doctors, lawyers and NRI couples returning home — each project approached with the cultural sensitivity Chennai expects.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you typeset in Tamil script correctly?",
        a: "Yes — our Tamil typography is set in proper Tamil script with correct conjunct behaviour, traditional ceremonial invocations and appropriate honorifics. We don't transliterate.",
      },
      {
        q: "Can you design for Iyer or Iyengar weddings?",
        a: "Yes — we have designed many traditional Tamil Brahmin wedding invitations across both Iyer and Iyengar communities, with sect-specific conventions and ceremonial language.",
      },
      {
        q: "Can the invite include temple-gopuram illustrations?",
        a: "Absolutely — temple architecture, kalasam, kolam patterns, banana leaves and mango leaves are common motifs in our Chennai wedding invitations.",
      },
      {
        q: "How fast can you deliver a Chennai wedding invitation?",
        a: "Most Chennai wedding invitations are delivered within 24 hours from final brief. Same-day expedite is available for urgent timelines.",
      },
      {
        q: "Do you ship printed stationery to Chennai?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Chennai pincode via Bluedart Express.",
      },
      {
        q: "Do you design for Chettiar or Mudaliar weddings too?",
        a: "Yes — we adapt typography, motifs and language conventions to honour every Tamil community's traditions — Chettiar, Mudaliar, Nadar, Reddy, Naidu and others.",
      },
    ],
  },
  {
    slug: "pune",
    city: "Pune",
    country: "IN",
    region: "Maharashtra",
    type: "india",
    metaTitle:
      "Wedding Invitations Pune | Marathi E-Invites in Pune | InvitationWala",
    metaDescription:
      "Premium wedding invitations in Pune — Marathi typography, peshwa-era heritage motifs, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Pune weddings carry both Maharashtrian tradition and modern cosmopolitan ease — from traditional Marathi weddings at heritage wadas to destination-style celebrations at vineyard resorts in Nashik and beyond. At InvitationWala we design wedding invitations in Pune that honour both the peshwa-era heritage and the city's contemporary creative culture.",
    sections: [
      {
        h2: "Pune weddings we design for",
        paragraphs: [
          "We design wedding invitations for Maharashtrian Brahmin (Deshastha, Kokanastha) weddings, Marathi-Maratha weddings, Saraswat weddings, North-Indian Pune-resident weddings, and modern inter-community weddings. Each invite is designed with appropriate community sensitivity — Marathi script set correctly, Ganesh aarti language used precisely, and ceremonial elements positioned per tradition.",
        ],
        bullets: [
          "Marathi typography in Devanagari with correct conventions",
          "Custom illustrations of Shaniwar Wada, Pune's peshwa heritage",
          "Ganesh, Tulsi, Mangalsutra ceremonial motifs",
          "Modern minimal aesthetic for design-savvy Pune couples",
          "WhatsApp delivery across Pune, PCMC and rural Maharashtra",
        ],
      },
      {
        h2: "Pune neighbourhoods and venues",
        paragraphs: [
          "We design invitations for couples across Koregaon Park, Kalyani Nagar, Aundh, Baner, Camp, Magarpatta, Hinjewadi and PCMC. Common venues include JW Marriott, Hyatt Regency, Conrad, ITC Maratha, Royal Orchid, and the many private wadas and vineyard estates in and around the city.",
        ],
      },
      {
        h2: "Why Pune couples choose us",
        paragraphs: [
          "Pune is a deeply intellectual city — couples here are often academics, IT founders, classical musicians, and second-generation Maharashtrian businessmen who value craft, tradition and design integrity. Our wedding invitations align naturally with this sensibility.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Marathi wedding invitations correctly?",
        a: "Yes — Marathi script is one of our most-used scripts, with correct Devanagari conjuncts, traditional honorifics and Marathi-specific ceremonial invocations.",
      },
      {
        q: "Can the invite include peshwa-heritage motifs?",
        a: "Yes — we design Pune wedding invitations with peshwa-era architectural references, Shaniwar Wada silhouettes, and traditional Maratha typography.",
      },
      {
        q: "Do you ship printed stationery to Pune?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Pune and PCMC pincode via Bluedart Express.",
      },
      {
        q: "Can you design for Marathi Brahmin and Maratha communities?",
        a: "Yes — we adapt typography, ceremonial language and motifs to honour each community's traditions appropriately.",
      },
      {
        q: "How fast is delivery for a Pune wedding invitation?",
        a: "Standard delivery is 24 hours from final brief. Same-day expedite is available for urgent timelines.",
      },
      {
        q: "Do you design for Punjabi or North Indian Pune weddings?",
        a: "Yes — Pune has many North Indian resident families, and we design Hindi-Punjabi wedding invitations alongside Marathi ones, sometimes bilingually for inter-community weddings.",
      },
    ],
  },
  {
    slug: "ahmedabad",
    city: "Ahmedabad",
    country: "IN",
    region: "Gujarat",
    type: "india",
    metaTitle:
      "Wedding Invitations Ahmedabad | Gujarati E-Invites | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations in Ahmedabad — Gujarati typography, vibrant colour palettes, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Ahmedabad weddings celebrate the vibrancy of Gujarati culture — rich colour palettes, intricate bandhani patterns, joyous garba evenings and the warmth of large Gujarati family gatherings. At InvitationWala we design wedding invitations in Ahmedabad that capture this celebratory spirit through bold typography, traditional motifs and modern motion.",
    sections: [
      {
        h2: "Gujarati wedding invitations we design",
        paragraphs: [
          "We design wedding invitations for Gujarati communities across all sects — Patidar, Jain, Brahmin, Vaishnav, Sindhi-Gujarati and inter-community weddings. Each invite features appropriate Gujarati typography (Devanagari-influenced script), traditional motifs (kalash, bandhani, mango), and the celebratory palette Gujarati weddings are loved for.",
        ],
        bullets: [
          "Gujarati typography in traditional script conventions",
          "Bandhani, kalash, mango motif vocabulary",
          "Custom illustration of Sabarmati or your venue",
          "Garba-night dedicated function card design",
          "WhatsApp delivery across Ahmedabad, Gandhinagar, Surat",
        ],
      },
      {
        h2: "Ahmedabad areas we serve",
        paragraphs: [
          "We design invitations for couples across Satellite, Bodakdev, Vastrapur, Prahlad Nagar, SG Highway and the wider Ahmedabad-Gandhinagar region. Common venues include the Hyatt Regency, Taj Skyline, Renaissance, ITC Narmada and traditional Patel/Jain community halls.",
        ],
      },
      {
        h2: "Why Ahmedabad couples choose us",
        paragraphs: [
          "Ahmedabad couples value warmth, family inclusion and design joy. Our wedding invitations celebrate that with vibrant palettes, generous family-name hierarchy, and motion that feels like the city's signature welcome.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Gujarati wedding invitations?",
        a: "Yes — Gujarati typography, motifs and ceremonial language are part of our standard offering. We design for Patidar, Jain, Brahmin and Vaishnav community weddings.",
      },
      {
        q: "Can you typeset in Gujarati script?",
        a: "Yes — we typeset in proper Gujarati script with correct conjunct behaviour and traditional honorifics.",
      },
      {
        q: "Do you design Jain wedding invitations?",
        a: "Yes — Jain wedding invitations have specific ceremonial conventions (no alcohol mentions, vegetarian-only language, Tirthankar invocations) which we honour precisely.",
      },
      {
        q: "Do you ship printed stationery to Ahmedabad?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to Ahmedabad, Gandhinagar and Gujarat-wide via Bluedart Express.",
      },
      {
        q: "Can the invite include garba night details?",
        a: "Absolutely — garba nights are a signature function in Gujarati weddings. We design dedicated garba function cards with appropriate iconography.",
      },
      {
        q: "How fast is delivery for an Ahmedabad invitation?",
        a: "Standard delivery is 24 hours from final brief. Same-day expedite is available.",
      },
    ],
  },
  {
    slug: "jaipur",
    city: "Jaipur",
    country: "IN",
    region: "Rajasthan",
    type: "india",
    metaTitle:
      "Wedding Invitations Jaipur | Royal Rajasthani E-Invites | InvitationWala",
    metaDescription:
      "Premium royal wedding invitations in Jaipur — heritage palace aesthetics, Rajasthani motifs, custom designed. Pink City weddings deserve InvitationWala.",
    heroImage: HERO_DEFAULT,
    intro:
      "Jaipur weddings are the closest modern weddings come to royal celebrations. Palace venues, heritage hotels, royal courtyards, and a design language drawn from the Pink City's centuries of architectural genius. At InvitationWala we design wedding invitations in Jaipur with the regal restraint the city demands — palace silhouettes, jaali patterns, deep magenta and gold palettes, and Rajasthani-traditional typography.",
    sections: [
      {
        h2: "Royal Jaipur wedding aesthetics",
        paragraphs: [
          "Our Jaipur wedding invitations draw from the visual library the city itself provides — the City Palace's mirror work, Hawa Mahal's jharokha windows, Amber Fort's frescoes, Jal Mahal's water palette and the famous pink walls of the old city. Every invitation features at least one architectural reference custom-illustrated for the couple.",
        ],
        bullets: [
          "Palace silhouettes & jharokha-window illustrations",
          "Jaali (lattice) decorative borders",
          "Magenta, ivory and gold royal palette",
          "Rajasthani-traditional typography conventions",
          "Custom motion inspired by palace ceremonial moments",
        ],
      },
      {
        h2: "Jaipur venues we design for",
        paragraphs: [
          "We design wedding invitations for weddings hosted at Jaipur's most-loved venues — Rambagh Palace, Jai Mahal Palace, Samode Palace, Fairmont Jaipur, ITC Rajputana, Taj Jai Mahal, Trident Jaipur and private heritage haveli venues across the city. Each venue gets its own custom architectural illustration in the invite.",
        ],
      },
      {
        h2: "Destination weddings in Jaipur",
        paragraphs: [
          "Jaipur is one of India's most-loved destination wedding cities. We design destination wedding invitation suites for couples bringing guests from across India and the diaspora — with multi-day itinerary cards, hotel booking links, palace tour suggestions and dedicated RSVP microsites that handle the logistics of a destination wedding.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you specialise in Jaipur destination wedding invitations?",
        a: "Yes — Jaipur destination weddings are one of our most-loved categories. We design complete invitation suites with multi-day itineraries, hotel booking links and palace illustrations.",
      },
      {
        q: "Can the invite include Rambagh or Samode Palace illustrations?",
        a: "Absolutely — every Jaipur destination invite typically features a custom illustration of the wedding venue's most recognisable architectural element.",
      },
      {
        q: "Do you design royal-aesthetic invitations specifically?",
        a: "Yes — see our dedicated Royal Wedding Invitations service for the full royal aesthetic offering. Many Jaipur couples specifically request this style.",
      },
      {
        q: "Do you typeset Marwari or Rajasthani script?",
        a: "Yes — we typeset in Devanagari with Rajasthani conventions, and can include Marwari ceremonial language and traditional invocations.",
      },
      {
        q: "Do you ship printed stationery to Jaipur venues?",
        a: "Yes — Stationery Wala ships welcome hampers, itinerary cards and printed stationery directly to Jaipur palace venues via secure courier.",
      },
      {
        q: "How early should we order Jaipur destination wedding invitations?",
        a: "Order save-the-dates 8-12 months before, and the full invitation suite 4-6 months before. Destination weddings need more lead time for guest travel planning.",
      },
    ],
  },
  {
    slug: "lucknow",
    city: "Lucknow",
    country: "IN",
    region: "Uttar Pradesh",
    type: "india",
    metaTitle:
      "Wedding Invitations Lucknow | Nawabi & Awadhi E-Invites | InvitationWala",
    metaDescription:
      "Premium wedding invitations in Lucknow — nawabi heritage, chikankari motifs, Urdu calligraphy. Custom designed in 24 hours. WhatsApp delivered.",
    heroImage: HERO_DEFAULT,
    intro:
      "Lucknow weddings carry the unmistakable grace of nawabi culture — Urdu poetry on every card, chikankari embroidery on every outfit, the soft palette of pearl and rose, and the unhurried elegance that the city has refined over three centuries. At InvitationWala we design wedding invitations in Lucknow that honour this heritage with Urdu calligraphy, Awadhi motifs and the considered restraint Lucknow weddings have always preferred.",
    sections: [
      {
        h2: "Lucknowi wedding aesthetics",
        paragraphs: [
          "Lucknow weddings, whether Hindu or Muslim, share a common refinement — restrained colour palettes, intricate detail, and a preference for craft over loudness. Our wedding invitations honour this with proper Urdu Nastaliq calligraphy, chikankari-pattern decorative borders, palette references to pearl, ivory and dusty rose, and architectural illustrations of Bara Imambara, Chota Imambara and Rumi Darwaza.",
        ],
        bullets: [
          "Urdu Nastaliq calligraphy with correct ligatures",
          "Chikankari-pattern decorative borders",
          "Pearl, ivory, dusty rose palette",
          "Bara Imambara & Rumi Darwaza architectural references",
          "Awadhi ceremonial poetry typeset correctly",
        ],
      },
      {
        h2: "Lucknow areas and venues",
        paragraphs: [
          "We design invitations for couples across Hazratganj, Gomti Nagar, Aliganj, Indira Nagar, Mahanagar and Cantt areas. Common venues include the Taj Lucknow, Renaissance, Vivanta, Lebua and historic havelis and Imambaras across the old city.",
        ],
      },
      {
        h2: "Why Lucknow couples choose us",
        paragraphs: [
          "Lucknow couples are deeply design-aware — many come from families where calligraphy, poetry and craft are inherited traditions. Our work resonates with this audience because we approach Urdu typography as poetry, not decoration.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you typeset Urdu correctly with proper Nastaliq calligraphy?",
        a: "Yes — Urdu Nastaliq is one of our most refined typography offerings, with correct ligature behaviour and traditional poetic conventions.",
      },
      {
        q: "Can you design Muslim wedding invitations for Lucknow?",
        a: "Yes — including nikah and walima cards, with appropriate Islamic invocations and Bismillah typesetting.",
      },
      {
        q: "Can you incorporate Lucknowi poetry into the invitation?",
        a: "Absolutely — Urdu sher and Awadhi couplets are often included in Lucknow wedding invitations. We typeset these with appropriate poetic conventions.",
      },
      {
        q: "Do you ship printed stationery to Lucknow?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Lucknow pincode via Bluedart Express.",
      },
      {
        q: "Can you design chikankari-pattern decorative borders?",
        a: "Yes — chikankari embroidery patterns are one of our most-loved Lucknow design elements, used as borders, monogram frames and seal designs.",
      },
      {
        q: "How fast is delivery for a Lucknow wedding invitation?",
        a: "Standard delivery is 24 hours from final brief. Same-day expedite is available for urgent timelines.",
      },
    ],
  },
  {
    slug: "kolkata",
    city: "Kolkata",
    country: "IN",
    region: "West Bengal",
    type: "india",
    metaTitle:
      "Wedding Invitations Kolkata | Bengali E-Invites | InvitationWala",
    metaDescription:
      "Premium Bengali wedding invitations in Kolkata — traditional alpana motifs, Bengali typography, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Kolkata weddings are quietly the most romantic of all Indian weddings — Bengali typography that flows like poetry, alpana threshold patterns drawn at dawn, the warmth of large extended Bengali families, and the gentle ceremonial rhythm that has shaped Bengali weddings for generations. At InvitationWala we design wedding invitations in Kolkata that honour this tradition with proper Bengali typography, traditional motifs and the considered elegance Kolkata weddings have always loved.",
    sections: [
      {
        h2: "Bengali wedding aesthetics in design",
        paragraphs: [
          "Bengali weddings are anchored in beautiful, distinct ceremonial moments — aiburo bhaat, gaye holud, the white-and-red saree, the bori bashi rituals. Our wedding invitations honour these moments with appropriate iconography, Bengali script set correctly, alpana decorative borders and palette choices drawn from traditional Bengali wedding colours (white, red, gold).",
        ],
        bullets: [
          "Bengali typography in proper script (no transliteration)",
          "Alpana decorative borders and threshold motifs",
          "White-and-red traditional Bengali palette",
          "Shankha-pola, sindoor, conch ceremonial motifs",
          "Custom illustration of Victoria Memorial or Howrah Bridge",
        ],
      },
      {
        h2: "Kolkata areas and venues",
        paragraphs: [
          "We design invitations for couples across Park Street, Alipore, Salt Lake, New Town and the wider Kolkata region. Common venues include the Oberoi Grand, ITC Sonar, Taj Bengal, Hyatt Regency, Park Hotel and traditional bonedi-bari houses across north Kolkata.",
        ],
      },
      {
        h2: "Why Kolkata couples choose us",
        paragraphs: [
          "Kolkata couples are intellectual, design-aware and deeply rooted in Bengali literary culture. They value typography, restraint and tradition. Our wedding invitations align with this naturally — we typeset Bengali script with the same care a literary publisher would, and our design language honours Bengali artistic heritage from Jamini Roy onwards.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you typeset in Bengali script?",
        a: "Yes — we typeset in proper Bengali script with correct conjunct behaviour, traditional ceremonial language and appropriate honorifics. We don't transliterate.",
      },
      {
        q: "Can you include alpana motifs?",
        a: "Yes — alpana threshold patterns are one of our most-loved Bengali wedding motifs. We hand-draw alpana borders, frames and seal designs.",
      },
      {
        q: "Do you design for bonedi-bari traditional Bengali weddings?",
        a: "Yes — we adapt typography, ceremonial language and motifs to honour bonedi-bari traditions appropriately.",
      },
      {
        q: "Do you ship printed stationery to Kolkata?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Kolkata pincode via Bluedart Express.",
      },
      {
        q: "Can the invite include Bengali poetry or Tagore verses?",
        a: "Absolutely — Rabindranath Tagore verses are often included in Bengali wedding invitations. We typeset these with appropriate poetic conventions.",
      },
      {
        q: "How fast is delivery for a Kolkata wedding invitation?",
        a: "Standard delivery is 24 hours from final brief. Same-day expedite is available for urgent timelines.",
      },
    ],
  },
  {
    slug: "surat",
    city: "Surat",
    country: "IN",
    region: "Gujarat",
    type: "india",
    metaTitle:
      "Wedding Invitations Surat | Gujarati Diamond-City E-Invites | InvitationWala",
    metaDescription:
      "Premium wedding invitations in Surat — Gujarati typography, diamond-city luxury aesthetic, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Surat weddings carry the unmistakable confidence of the diamond city — rich palettes, generous family hierarchies, and a wedding scale that often crosses thousands of guests. At InvitationWala we design wedding invitations in Surat that honour this scale with grace — Gujarati typography set correctly, jewellery-inspired motion, and palettes that draw from the city's signature diamond and gold aesthetic.",
    sections: [
      {
        h2: "Surat wedding aesthetics we design",
        paragraphs: [
          "Surat weddings often have larger guest counts and more functions than the average Indian wedding — we design invitation suites that scale gracefully across thousands of broadcasts. Each function card maintains design consistency while introducing a unique colour accent and motif appropriate to that ceremony.",
        ],
        bullets: [
          "Gujarati typography in correct script conventions",
          "Diamond, jewellery and gold motif vocabulary",
          "Rich saturated palettes for large-scale weddings",
          "Multi-function suites for 6-8 function celebrations",
          "WhatsApp broadcast-optimised file sizes",
        ],
      },
      {
        h2: "Surat areas and venues",
        paragraphs: [
          "We design invitations for couples across Vesu, Adajan, Citylight, Athwa, Piplod and the wider Surat region. Surat weddings often happen at large convention centres, private estates and dedicated wedding venues — each invite is designed with appropriate venue illustration.",
        ],
      },
      {
        h2: "Why Surat couples choose us",
        paragraphs: [
          "Surat couples value generosity, craft and scale. Our wedding invitations bring all three — generous family-name hierarchy, careful typography, and motion that holds up across the very large broadcast lists Surat weddings typically have.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design for large Surat weddings with many functions?",
        a: "Yes — Surat weddings often have 6-8 functions and very large guest lists. Our invitation suites scale gracefully across all of them while maintaining design consistency.",
      },
      {
        q: "Can the invite be in Gujarati script?",
        a: "Yes — we typeset in proper Gujarati script with correct conjunct behaviour and traditional honorifics.",
      },
      {
        q: "Do you ship printed stationery to Surat?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to every Surat pincode via Bluedart Express.",
      },
      {
        q: "Can the invite mention diamond-trade family heritage?",
        a: "Many Surat families have multi-generational diamond trade heritage. We can incorporate appropriate motifs and family hierarchy that honours this legacy.",
      },
      {
        q: "How fast is delivery for a Surat wedding invitation?",
        a: "Standard delivery is 24 hours from final brief. Same-day expedite is available.",
      },
      {
        q: "Can you handle 5000+ guest broadcasts?",
        a: "Yes — our files are sized and compressed to broadcast successfully even to very large guest lists across WhatsApp's broadcast limits.",
      },
    ],
  },
  {
    slug: "indore",
    city: "Indore",
    country: "IN",
    region: "Madhya Pradesh",
    type: "india",
    metaTitle:
      "Wedding Invitations Indore | Premium E-Invites in Indore | InvitationWala",
    metaDescription:
      "Premium wedding invitations in Indore — Marwari and Hindi typography, custom designed. WhatsApp delivered in 24 hours. Trusted across MP.",
    heroImage: HERO_DEFAULT,
    intro:
      "Indore weddings combine the warmth of central Indian hospitality with the design sensibility of an emerging metropolitan city. From traditional Marwari and Maheshwari weddings to modern destination-style celebrations, Indore couples appreciate craft, restraint and the considered design language we bring. At InvitationWala we design wedding invitations in Indore that feel right whether your wedding leans traditional or contemporary.",
    sections: [
      {
        h2: "Indore wedding aesthetics we design",
        paragraphs: [
          "Indore is home to several major Indian communities — Marwari, Maheshwari, Sindhi, Gujarati and Maharashtrian families have all settled here over generations. We design wedding invitations that honour each community's specific traditions with appropriate typography, motifs and ceremonial language.",
        ],
        bullets: [
          "Hindi & Marwari typography in correct conventions",
          "Maheshwari and Sindhi community-specific design",
          "Custom illustration of Rajwada or your venue",
          "Multi-function suites for traditional Indore weddings",
          "WhatsApp delivery across Indore, Bhopal, Madhya Pradesh",
        ],
      },
      {
        h2: "Indore areas and venues",
        paragraphs: [
          "We design invitations for couples across Vijay Nagar, Palasia, AB Road, Bypass Road and the wider Indore region. Common venues include Marriott, Radisson, Sayaji, Effotel and traditional community halls across the city.",
        ],
      },
      {
        h2: "Why Indore couples choose us",
        paragraphs: [
          "Indore couples are increasingly design-aware — many work in IT, business, healthcare or have studied abroad. They appreciate craft and precision in design, which our wedding invitations bring naturally.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design for Marwari and Maheshwari weddings in Indore?",
        a: "Yes — we adapt typography, ceremonial language and motifs to honour each community's traditions appropriately.",
      },
      {
        q: "Can the invite be in Hindi or Marwari script?",
        a: "Yes — we typeset in Devanagari with appropriate community-specific conventions and honorifics.",
      },
      {
        q: "Do you ship printed stationery to Indore?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to Indore via Bluedart Express.",
      },
      {
        q: "Can you mention Indore venues like Marriott or Sayaji?",
        a: "Yes — we typeset venue names correctly and can include venue illustrations as part of luxury and premium suites.",
      },
      {
        q: "How fast is delivery for an Indore wedding invitation?",
        a: "Standard delivery is 24 hours from final brief.",
      },
      {
        q: "Do you design for Sindhi weddings in Indore?",
        a: "Yes — Sindhi wedding traditions are honoured with appropriate ceremonial language and community-specific motifs.",
      },
    ],
  },
  {
    slug: "chandigarh",
    city: "Chandigarh",
    country: "IN",
    region: "Punjab/Haryana",
    type: "india",
    metaTitle:
      "Wedding Invitations Chandigarh | Punjabi E-Invites | InvitationWala",
    metaDescription:
      "Premium Punjabi wedding invitations in Chandigarh — Gurmukhi typography, Sikh ceremonies, custom designed. WhatsApp delivered in 24 hours.",
    heroImage: HERO_DEFAULT,
    intro:
      "Chandigarh weddings are warm, vibrant and unmistakably Punjabi. From traditional Sikh Anand Karaj ceremonies at Gurudwaras to grand Punjabi receptions at the city's iconic hotel ballrooms, Chandigarh sets a celebratory standard for North Indian weddings. At InvitationWala we design wedding invitations in Chandigarh with proper Gurmukhi typography, Sikh ceremonial conventions and the joyful energy Punjabi weddings carry.",
    sections: [
      {
        h2: "Punjabi wedding aesthetics in design",
        paragraphs: [
          "Punjabi weddings are big, joyful, celebratory affairs — and our wedding invitations match that energy through palettes that lean rich and warm, typography that carries traditional Punjabi-script grace, and motion that opens with the energy of dhol drums or the quietude of Gurbani depending on the wedding's tone.",
        ],
        bullets: [
          "Gurmukhi typography in correct conventions",
          "Ek Onkar and Sikh ceremonial motifs",
          "Sufi-Punjabi poetry typesetting",
          "Custom illustration of Rock Garden or your venue",
          "WhatsApp delivery across Chandigarh, Punjab, Haryana",
        ],
      },
      {
        h2: "Chandigarh areas and venues",
        paragraphs: [
          "We design invitations for couples across Sectors 8-46, Panchkula, Mohali and the Tricity region. Common venues include the Taj Chandigarh, JW Marriott Chandigarh, Hyatt Regency, Lalit, Park Plaza and traditional gurudwaras across the city.",
        ],
      },
      {
        h2: "Why Chandigarh couples choose us",
        paragraphs: [
          "Chandigarh couples value family, tradition and design that doesn't compromise. Our wedding invitations honour all three — appropriate Sikh and Punjabi traditions, large family hierarchies typeset correctly, and design that holds up under the scrutiny of design-aware Punjabi families.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Sikh wedding invitations correctly?",
        a: "Yes — Sikh wedding invitations include Ek Onkar, Gurmukhi script, traditional Sikh invocations and appropriate ceremonial conventions for Anand Karaj.",
      },
      {
        q: "Can you typeset Gurmukhi script?",
        a: "Yes — we typeset in proper Gurmukhi with correct conjunct behaviour and traditional honorifics.",
      },
      {
        q: "Do you design for Punjabi Hindu weddings too?",
        a: "Yes — we design for every Punjabi community including Punjabi Hindu, Sikh, Khatri and Arora weddings.",
      },
      {
        q: "Do you ship printed stationery to Chandigarh?",
        a: "Yes — Stationery Wala ships premium printed wedding stationery to Chandigarh, Mohali and Panchkula via Bluedart Express.",
      },
      {
        q: "Can the invite include Sufi-Punjabi poetry?",
        a: "Absolutely — Sufi-Punjabi sher and Punjabi couplets are often included in Punjabi wedding invitations. We typeset these with appropriate poetic conventions.",
      },
      {
        q: "How fast is delivery for a Chandigarh wedding invitation?",
        a: "Standard delivery is 24 hours from final brief.",
      },
    ],
  },
  {
    slug: "goa",
    city: "Goa",
    country: "IN",
    region: "Goa",
    type: "india",
    metaTitle:
      "Wedding Invitations Goa | Destination Wedding E-Invites | InvitationWala",
    metaDescription:
      "Premium destination wedding invitations in Goa — beach and Portuguese-heritage aesthetics, custom designed. WhatsApp delivered. Ship welcome hampers to Goa.",
    heroImage: HERO_DEFAULT,
    intro:
      "Goa weddings are India's most-loved destination weddings — beach ceremonies at sunset, Portuguese-heritage venues in Fontainhas, vineyard receptions in the interior, and the joyful relaxed energy that only Goa offers. At InvitationWala we design wedding invitations in Goa that honour both the beach-side relaxed elegance and the Portuguese-Indian architectural heritage that makes Goa weddings unforgettable.",
    sections: [
      {
        h2: "Goa wedding aesthetics we design",
        paragraphs: [
          "Goa weddings span two distinct aesthetic worlds — the beach-resort look (sand, sea, tropical palette, light typography) and the Portuguese-heritage look (azulejo tiles, deep cobalt and white, Indo-Portuguese architecture). Our wedding invitations are designed to match whichever world your Goa wedding inhabits.",
        ],
        bullets: [
          "Beach-resort or Portuguese-heritage aesthetic options",
          "Custom illustration of your Goa venue",
          "Azulejo-tile decorative pattern options",
          "Tropical palm and sea palette references",
          "Goan-traditional ceremonial conventions if needed",
        ],
      },
      {
        h2: "Goa wedding venues we design for",
        paragraphs: [
          "We design wedding invitations for weddings at Taj Exotica, Park Hyatt, W Goa, Alila Diwa, Grand Hyatt, Leela Goa, Renaissance Goa, and the many private villas, Portuguese-heritage homes and beach resort venues across North and South Goa.",
        ],
      },
      {
        h2: "Destination wedding logistics for Goa",
        paragraphs: [
          "Goa weddings require full destination wedding invitation suites — save-the-date 8-12 months ahead, main invite 4-6 months ahead, full itinerary with arrival flights, hotel booking links, transport pickups, and pre/post-wedding sightseeing recommendations. Our destination wedding suites handle all of this in one cohesive design.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you specialise in Goa destination wedding invitations?",
        a: "Yes — Goa is one of our most-loved destination wedding markets. We design complete suites with venue illustrations, multi-day itineraries and full RSVP microsites.",
      },
      {
        q: "Can the invite include Goa venue illustrations?",
        a: "Absolutely — every Goa wedding invite typically features a custom illustration of the venue's most recognisable architectural element (palms, beachfront, Portuguese facade).",
      },
      {
        q: "Do you ship welcome hampers to Goa venues?",
        a: "Yes — Stationery Wala ships welcome hampers, itinerary cards and printed stationery directly to your Goa venue via secure courier.",
      },
      {
        q: "Can you design Portuguese-heritage aesthetic invitations?",
        a: "Yes — azulejo tile patterns, deep cobalt and white palettes, and Indo-Portuguese architectural references are part of our standard Goa-aesthetic offering.",
      },
      {
        q: "How early should we order a Goa destination invite?",
        a: "Order save-the-dates 8-12 months before, full invite suite 4-6 months before. Destination weddings need lead time for guest travel planning.",
      },
      {
        q: "Can you design for Christian/Catholic Goan weddings?",
        a: "Yes — Catholic Goan wedding invitations have specific ceremonial conventions which we honour appropriately (church ceremony language, Portuguese-influenced typography).",
      },
    ],
  },
  // ============ INTERNATIONAL ============
  {
    slug: "dubai",
    city: "Dubai",
    country: "AE",
    region: "UAE",
    type: "international",
    metaTitle:
      "Wedding Invitations Dubai | Indian E-Invites for Dubai | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations for Indian weddings in Dubai — custom designed, WhatsApp delivered. Ship welcome hampers to Dubai venues.",
    heroImage: HERO_DEFAULT,
    intro:
      "Dubai weddings are some of the most ambitious Indian weddings happening worldwide — destination celebrations at the Burj Al Arab, Atlantis the Palm, Bvlgari Resort and Address Downtown bring together guests from India, the GCC and the global diaspora. At InvitationWala we design wedding invitations for Indian weddings in Dubai with the production value the city deserves — cinematic motion, considered typography and full multi-day itinerary handling.",
    sections: [
      {
        h2: "Dubai Indian wedding aesthetics",
        paragraphs: [
          "Dubai Indian weddings often blend Indian tradition with international destination-wedding production value. Our wedding invitations honour both — the cultural specificity of the Indian wedding ceremonies and the international design polish Dubai venues bring.",
        ],
        bullets: [
          "Indian + international design language blended cleanly",
          "Custom illustration of Burj Al Arab, Atlantis or your venue",
          "English-Hindi-Urdu trilingual typesetting options",
          "Multi-day destination wedding itinerary cards",
          "Ship welcome hampers and stationery to Dubai venues",
        ],
      },
      {
        h2: "Dubai venues we design for",
        paragraphs: [
          "We design wedding invitations for weddings at Burj Al Arab, Atlantis the Palm, Bvlgari Resort & Residences, Address Downtown, Address Sky View, Armani Hotel, Madinat Jumeirah, Four Seasons Resort, Waldorf Astoria DIFC, and Caesars Palace Dubai. Each venue gets a custom architectural illustration in the invite.",
        ],
      },
      {
        h2: "NRI couples in Dubai",
        paragraphs: [
          "We work extensively with the Indian diaspora in Dubai — second-generation Indian-origin families, NRI professionals from across the GCC, and couples planning Dubai weddings while based in India. The entire process happens on WhatsApp on Dubai time, with delivery to your Dubai address or venue.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for Dubai?",
        a: "Yes — Dubai is one of our most active international markets. We design for Indian weddings, NRI families and destination weddings in Dubai.",
      },
      {
        q: "Can the invite mention Dubai venues like Burj Al Arab?",
        a: "Absolutely — we typeset venue names correctly and include custom illustrations of Burj Al Arab, Atlantis the Palm, Bvlgari Resort and other iconic Dubai venues.",
      },
      {
        q: "Do you ship printed wedding stationery to Dubai?",
        a: "Yes — Stationery Wala ships welcome hampers, itinerary cards and printed wedding stationery to Dubai via DHL Express. Delivery is typically 7-10 business days.",
      },
      {
        q: "Can you design trilingual English-Hindi-Urdu invitations?",
        a: "Yes — trilingual invitations are common for Dubai Indian weddings, especially for Muslim Indian families. We typeset all three scripts together correctly.",
      },
      {
        q: "Do you work on Dubai time zones?",
        a: "Yes — our team takes WhatsApp calls between 9 AM-11 PM Dubai time, and we deliver invites that align with Dubai wedding schedules.",
      },
      {
        q: "How early should we order a Dubai destination wedding invitation?",
        a: "Order save-the-dates 8-12 months before and full invite suite 4-6 months before. Dubai weddings need lead time for international guest travel.",
      },
    ],
  },
  {
    slug: "abu-dhabi",
    city: "Abu Dhabi",
    country: "AE",
    region: "UAE",
    type: "international",
    metaTitle:
      "Wedding Invitations Abu Dhabi | Indian E-Invites for Abu Dhabi | InvitationWala",
    metaDescription:
      "Premium digital wedding invitations for Indian weddings in Abu Dhabi — custom designed, WhatsApp delivered. Trusted by the UAE Indian diaspora.",
    heroImage: HERO_DEFAULT,
    intro:
      "Abu Dhabi Indian weddings carry a quieter, more considered elegance than Dubai — palace venues like Emirates Palace, the architectural grandeur of the Sheikh Zayed Mosque setting (for cultural events), and the warmth of the UAE Indian community. At InvitationWala we design wedding invitations for Abu Dhabi with appropriate cultural sensitivity and international production polish.",
    sections: [
      {
        h2: "Abu Dhabi Indian wedding aesthetics",
        paragraphs: [
          "Abu Dhabi venues bring a particular gravitas — the Emirates Palace, the St. Regis Saadiyat, the Ritz-Carlton Grand Canal, the Anantara Eastern Mangroves. Our wedding invitations are designed to honour the venue's stature with appropriate restraint and cinematic motion.",
        ],
        bullets: [
          "Custom illustration of Emirates Palace, Sheikh Zayed Mosque, or your venue",
          "English-Hindi-Urdu trilingual typesetting",
          "Multi-day destination wedding itinerary cards",
          "Ship welcome hampers and stationery to Abu Dhabi venues",
          "WhatsApp delivery on UAE time zones",
        ],
      },
      {
        h2: "Abu Dhabi venues we design for",
        paragraphs: [
          "Common Abu Dhabi venues include Emirates Palace, St. Regis Saadiyat Island, Ritz-Carlton Grand Canal, Anantara Eastern Mangroves, Yas Hotel, Park Hyatt Saadiyat and Jumeirah at Etihad Towers.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for Abu Dhabi?",
        a: "Yes — we design Indian wedding invitations for couples celebrating in Abu Dhabi, with appropriate cultural sensitivity and international polish.",
      },
      {
        q: "Can the invite include Emirates Palace illustrations?",
        a: "Absolutely — venue illustrations are part of our standard luxury and premium service.",
      },
      {
        q: "Do you ship printed stationery to Abu Dhabi?",
        a: "Yes — Stationery Wala ships to Abu Dhabi via DHL Express, typically within 7-10 business days.",
      },
      {
        q: "Can you handle Muslim Indian wedding invitations for Abu Dhabi?",
        a: "Yes — we include Bismillah, Islamic-traditional motifs and correct Urdu/Arabic typography conventions when appropriate.",
      },
      {
        q: "Do you design destination wedding suites for Abu Dhabi?",
        a: "Yes — complete suites with venue illustration, multi-day itinerary, hotel booking links and RSVP microsite.",
      },
      {
        q: "Can the invite mention multiple venue addresses?",
        a: "Yes — multi-venue weddings (nikah at one location, walima at another) are common in Abu Dhabi. We typeset each venue clearly.",
      },
    ],
  },
  {
    slug: "london",
    city: "London",
    country: "GB",
    region: "United Kingdom",
    type: "international",
    metaTitle:
      "Wedding Invitations London | Indian E-Invites in London | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in London — custom designed for British Asian weddings. WhatsApp delivered. Ship stationery to London venues.",
    heroImage: HERO_DEFAULT,
    intro:
      "London Indian weddings are a category all their own — second-generation British Asian families celebrating with both heritage and contemporary British wedding traditions, often across multiple venues from Surrey country estates to West London Gurudwaras to Mayfair hotels. At InvitationWala we design wedding invitations in London that bridge both worlds — Indian cultural specificity with British design polish.",
    sections: [
      {
        h2: "British Asian wedding aesthetics we design",
        paragraphs: [
          "London Indian weddings often blend tradition (Sikh Anand Karaj, Hindu vedic ceremonies, Muslim nikah) with British wedding conventions (registry office ceremonies, English garden receptions, formal sit-down dinners). Our wedding invitations are designed to bridge both worlds gracefully.",
        ],
        bullets: [
          "British-Indian dual aesthetic with English typography polish",
          "Punjabi/Gujarati/Tamil + English bilingual typesetting",
          "Custom illustration of London landmarks or your UK venue",
          "Multi-venue invite handling (Gurudwara, hotel, registry office)",
          "Ship printed stationery to UK addresses via DHL",
        ],
      },
      {
        h2: "London venues we design for",
        paragraphs: [
          "We design wedding invitations for British Asian weddings at venues across the UK — The Dorchester, Claridge's, The Savoy, Mandarin Oriental Hyde Park, Royal Lancaster, Pennyhill Park, Stoke Park, Hilton Park Lane, Hedsor House, Hampton Court House and many private estates across Surrey, Hertfordshire, Buckinghamshire and Berkshire.",
        ],
      },
      {
        h2: "Diaspora couples in London",
        paragraphs: [
          "We work extensively with British Asian couples in London — second and third-generation families celebrating Indian heritage weddings, NRI Indian couples relocating for marriage, and inter-cultural couples blending Indian and British family traditions. The entire process happens on WhatsApp on UK time, with delivery to your London address.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for the UK?",
        a: "Yes — London and the wider UK is one of our most active international markets. We design for British Asian, NRI and inter-cultural Indian weddings.",
      },
      {
        q: "Can the invite include London or UK venue illustrations?",
        a: "Absolutely — venue illustrations of UK country estates, London hotels and Gurudwaras are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to the UK?",
        a: "Yes — Stationery Wala ships welcome hampers, itinerary cards and printed wedding stationery to UK addresses via DHL Express. Delivery is 7-10 business days.",
      },
      {
        q: "Can you design multi-venue wedding invitations (Gurudwara + hotel)?",
        a: "Yes — multi-venue invitations are common for UK Indian weddings. We typeset each venue clearly with timing, dress code and transport details.",
      },
      {
        q: "Do you work on UK time zones?",
        a: "Yes — our team takes WhatsApp calls on UK time and delivers invites that align with UK wedding schedules.",
      },
      {
        q: "Can you design for Sikh Anand Karaj invitations in London?",
        a: "Yes — Sikh wedding invitations with Ek Onkar, Gurmukhi script and traditional conventions are one of our most loved London formats.",
      },
    ],
  },
  {
    slug: "birmingham",
    city: "Birmingham",
    country: "GB",
    region: "United Kingdom",
    type: "international",
    metaTitle:
      "Wedding Invitations Birmingham UK | Indian E-Invites | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Birmingham UK — custom designed for British Asian weddings. WhatsApp delivered. Ship to UK Midlands.",
    heroImage: HERO_DEFAULT,
    intro:
      "Birmingham hosts one of the largest South Asian communities outside India — and Birmingham Indian weddings carry that depth of community, ceremony and celebration. From Sikh weddings at the city's iconic Gurudwaras to Hindu and Muslim ceremonies across the Midlands, our wedding invitations honour Birmingham's distinctly British Asian wedding culture with appropriate sensitivity and design polish.",
    sections: [
      {
        h2: "Birmingham British Asian aesthetics",
        paragraphs: [
          "Birmingham weddings often happen across multiple days and multiple venues — Sikh Anand Karaj morning at a Gurudwara, civil registry ceremony at a Birmingham city hall, and grand reception at hotels like the Hyatt Regency, Hilton Metropole or country estate venues across the Midlands. Our wedding invitations are designed to carry this multi-venue, multi-day structure gracefully.",
        ],
        bullets: [
          "Punjabi/Gujarati/Tamil + English bilingual typesetting",
          "Sikh Anand Karaj, Hindu, Muslim ceremony formats",
          "Custom illustration of Birmingham landmarks or your venue",
          "Multi-venue invite cards for Gurudwara + hotel + registry",
          "Ship printed stationery to Birmingham via DHL",
        ],
      },
      {
        h2: "Birmingham and Midlands venues",
        paragraphs: [
          "We design wedding invitations for British Asian weddings across Birmingham, Wolverhampton, Coventry, Leicester, Nottingham and the wider Midlands. Common venues include the Hyatt Regency Birmingham, Hilton Birmingham Metropole, Park Regis, Edgbaston Park Hotel, ICC Birmingham, and the many country estates across Warwickshire and Worcestershire.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design for British Asian weddings in Birmingham?",
        a: "Yes — Birmingham and the Midlands is a significant market for our British Asian wedding invitations. We design for Sikh, Hindu and Muslim Indian weddings.",
      },
      {
        q: "Can the invite include Birmingham venue illustrations?",
        a: "Absolutely — venue illustrations of Birmingham hotels and country estates are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Birmingham?",
        a: "Yes — Stationery Wala ships to Birmingham and the Midlands via DHL Express, typically 7-10 business days.",
      },
      {
        q: "Can you handle multi-venue UK weddings?",
        a: "Yes — multi-venue invitations are standard for UK Indian weddings. We typeset each venue clearly with timing, dress code and transport.",
      },
      {
        q: "Do you typeset Gurmukhi for Sikh Birmingham weddings?",
        a: "Yes — Gurmukhi script with correct ceremonial conventions for Sikh Anand Karaj is part of our standard offering.",
      },
      {
        q: "How fast is delivery for a UK wedding invitation?",
        a: "Digital delivery is 24 hours from final brief. Printed UK stationery is 7-10 days via DHL.",
      },
    ],
  },
  {
    slug: "manchester",
    city: "Manchester",
    country: "GB",
    region: "United Kingdom",
    type: "international",
    metaTitle:
      "Wedding Invitations Manchester | Indian E-Invites Manchester | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Manchester — custom designed for British Asian families in the North West. WhatsApp delivered.",
    heroImage: HERO_DEFAULT,
    intro:
      "Manchester and the North West of England host vibrant South Asian wedding communities — and Manchester Indian weddings carry the warmth and design-confidence the city is known for. At InvitationWala we design wedding invitations in Manchester with appropriate British Asian cultural sensitivity and international production polish.",
    sections: [
      {
        h2: "Manchester Indian wedding aesthetics",
        paragraphs: [
          "Manchester Indian weddings often happen across venues like the Hilton Manchester Deansgate, Lowry Hotel, Midland Hotel, and country estates across Cheshire and Lancashire. Our wedding invitations are designed to feel right whether your wedding leans traditional or contemporary British Asian.",
        ],
        bullets: [
          "Punjabi/Gujarati/Tamil + English bilingual typesetting",
          "Sikh Anand Karaj and Hindu ceremony formats",
          "Custom illustration of Manchester landmarks or your venue",
          "Multi-venue UK invite handling",
          "Ship printed stationery to Manchester via DHL",
        ],
      },
      {
        h2: "Manchester and North West venues",
        paragraphs: [
          "We design wedding invitations for British Asian weddings at venues across Manchester, Liverpool, Leeds, Bradford and the wider North West. Common venues include the Lowry Hotel, Midland Hotel, Hilton Manchester Deansgate, Cottons Hotel, Crewe Hall, Mottram Hall and many estate venues across Cheshire.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design for British Asian weddings in Manchester?",
        a: "Yes — Manchester and the North West is part of our active UK market. We design for Sikh, Hindu, Muslim and inter-cultural Indian weddings.",
      },
      {
        q: "Can you include Manchester venue illustrations?",
        a: "Yes — venue illustrations of Manchester hotels and country estates are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Manchester?",
        a: "Yes — Stationery Wala ships to Manchester via DHL Express, typically 7-10 business days.",
      },
      {
        q: "Can you typeset Gurmukhi for Manchester Sikh weddings?",
        a: "Yes — Gurmukhi script with Sikh Anand Karaj conventions is part of our standard offering.",
      },
      {
        q: "Do you work with UK destination wedding suites?",
        a: "Yes — multi-day UK destination wedding invitations (country estate weekends) are increasingly popular and we design them as full suites.",
      },
      {
        q: "How fast is delivery for a Manchester wedding invitation?",
        a: "Digital delivery is 24 hours from final brief.",
      },
    ],
  },
  {
    slug: "new-york",
    city: "New York",
    country: "US",
    region: "United States",
    type: "international",
    metaTitle:
      "Wedding Invitations New York | Indian E-Invites NYC | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in New York — custom designed for NRI Indian-American weddings. WhatsApp delivered. Ship stationery to NYC.",
    heroImage: HERO_DEFAULT,
    intro:
      "New York Indian weddings combine the production polish of America's most ambitious city with the depth of Indian wedding heritage. From Manhattan ballrooms to Long Island estates to upstate destination venues, NYC Indian weddings raise the bar. At InvitationWala we design wedding invitations in New York that match this scale with cinematic motion and considered typography.",
    sections: [
      {
        h2: "NYC Indian wedding aesthetics",
        paragraphs: [
          "New York Indian weddings often happen across multiple days and venues — engagement ceremonies in Jackson Heights or Edison, mehndi in private homes, sangeet at NYC ballrooms, ceremony at temples or hotels, and reception at iconic venues like the Plaza, the Pierre or the Mandarin Oriental. Our wedding invitations honour all of these touchpoints.",
        ],
        bullets: [
          "Hindi/Gujarati/Tamil + English bilingual typesetting",
          "Custom illustration of NYC skyline or your venue",
          "Multi-day NYC destination wedding suites",
          "Ship printed stationery to NYC via DHL/FedEx",
          "WhatsApp delivery on EST time zones",
        ],
      },
      {
        h2: "NYC venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across NYC and the tri-state area — The Plaza, The Pierre, Mandarin Oriental, Cipriani, St. Regis, Lotte New York Palace, Crystal Plaza, The Rockleigh, Carlyle on the Green, and many private estates across Long Island, Westchester and New Jersey.",
        ],
      },
      {
        h2: "NRI couples in New York",
        paragraphs: [
          "We work extensively with the Indian-American community in NYC — second and third-generation Indian-American families, NRI tech professionals, finance industry couples, and inter-cultural couples blending Indian heritage with American wedding traditions. The entire process happens on WhatsApp on EST time.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for New York?",
        a: "Yes — NYC and the tri-state area is one of our most active international markets. We design for Indian-American, NRI and inter-cultural weddings.",
      },
      {
        q: "Can the invite include NYC skyline illustrations?",
        a: "Absolutely — custom NYC skyline illustrations, Manhattan landmark references and venue illustrations are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to NYC?",
        a: "Yes — Stationery Wala ships welcome hampers and printed wedding stationery to NYC via DHL/FedEx Express, typically 5-7 business days.",
      },
      {
        q: "Can you handle multi-day NYC Indian weddings?",
        a: "Yes — multi-day, multi-venue NYC Indian weddings are common and we design full destination wedding invitation suites for them.",
      },
      {
        q: "Do you work on EST time zones?",
        a: "Yes — our team takes WhatsApp calls between 9 AM-11 PM EST and delivers invites that align with NYC wedding schedules.",
      },
      {
        q: "Can you design for Indian-American inter-cultural weddings?",
        a: "Yes — inter-cultural weddings (Indian + American, Indian + Jewish, Indian + Italian, etc.) are a growing part of our NYC portfolio.",
      },
    ],
  },
  {
    slug: "new-jersey",
    city: "New Jersey",
    country: "US",
    region: "United States",
    type: "international",
    metaTitle:
      "Wedding Invitations New Jersey | Indian E-Invites NJ | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in New Jersey — custom designed for NRI Indian-American weddings. WhatsApp delivered. Ship to NJ.",
    heroImage: HERO_DEFAULT,
    intro:
      "New Jersey is home to one of the largest Indian-American communities in the US — and NJ Indian weddings carry the depth of community, tradition and celebration that follows. From temple weddings in Edison and Iselin to grand receptions at country club venues across Bergen, Morris and Somerset counties, our wedding invitations honour New Jersey's distinctly Indian-American wedding culture.",
    sections: [
      {
        h2: "New Jersey Indian wedding aesthetics",
        paragraphs: [
          "NJ Indian weddings often happen across multiple days — engagement at a temple, mehndi at home, sangeet at a banquet hall, ceremony at a temple or hotel, reception at a country club. Our wedding invitations are designed to carry this multi-day, multi-venue structure with cohesive design language throughout.",
        ],
        bullets: [
          "Hindi/Gujarati/Tamil/Punjabi + English bilingual typesetting",
          "Custom illustration of your NJ temple or country club venue",
          "Multi-day destination wedding suites",
          "Ship printed stationery to NJ via FedEx/DHL",
          "WhatsApp delivery on EST time zones",
        ],
      },
      {
        h2: "NJ venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across New Jersey — The Rockleigh, The Venetian, Crystal Plaza, Park Chateau, Pleasantdale Chateau, Brownstone, Westmount Country Club, and the many BAPS/Hindu temples across Edison, Iselin, Robbinsville and Atlantic City.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for New Jersey?",
        a: "Yes — NJ is one of our most active international markets, especially for second-generation Indian-American families.",
      },
      {
        q: "Can the invite include NJ venue illustrations?",
        a: "Absolutely — venue illustrations of country clubs, temples and banquet halls in NJ are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to NJ?",
        a: "Yes — Stationery Wala ships to NJ via DHL/FedEx Express, typically 5-7 business days.",
      },
      {
        q: "Can you typeset for South Indian temple weddings in NJ?",
        a: "Yes — Tamil, Telugu, Malayalam and Kannada typesetting with appropriate temple-wedding ceremonial conventions.",
      },
      {
        q: "Do you handle BAPS temple wedding invitations?",
        a: "Yes — BAPS Hindu wedding invitations have specific ceremonial conventions which we honour appropriately.",
      },
      {
        q: "How fast is delivery for an NJ wedding invitation?",
        a: "Digital delivery is 24 hours from final brief.",
      },
    ],
  },
  {
    slug: "toronto",
    city: "Toronto",
    country: "CA",
    region: "Canada",
    type: "international",
    metaTitle:
      "Wedding Invitations Toronto | Indian E-Invites Canada | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Toronto — custom designed for Indo-Canadian weddings. WhatsApp delivered. Ship welcome hampers to Toronto.",
    heroImage: HERO_DEFAULT,
    intro:
      "Toronto is home to one of the largest South Asian communities outside India, and Toronto Indian weddings carry significant cultural depth — Sikh Anand Karaj ceremonies at gurudwaras across Brampton and Mississauga, Hindu and Tamil weddings at temples across the GTA, and inter-cultural celebrations across Toronto's diverse venue landscape. At InvitationWala we design wedding invitations in Toronto with appropriate cultural sensitivity and international polish.",
    sections: [
      {
        h2: "Toronto Indian wedding aesthetics",
        paragraphs: [
          "Toronto Indian weddings often span multiple venues — gurudwara/temple ceremony in the morning, civil/registry ceremony in the afternoon, grand reception at a Toronto hotel ballroom or country estate in the evening. Our wedding invitations are designed to carry this multi-venue, multi-day structure gracefully.",
        ],
        bullets: [
          "Punjabi/Gujarati/Tamil + English bilingual typesetting",
          "Sikh Anand Karaj, Hindu, Tamil ceremony formats",
          "Custom illustration of Toronto landmarks or your venue",
          "Multi-venue Canadian invite handling",
          "Ship printed stationery to Toronto via DHL",
        ],
      },
      {
        h2: "Toronto/GTA venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across Toronto, Mississauga, Brampton, Markham, Vaughan and the wider GTA. Common venues include the Liberty Grand, the Eglinton Grand, Fairmont Royal York, Pearson Convention Centre, the many gurudwaras across Brampton, and Hindu/Tamil temples across the GTA.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design for Indo-Canadian weddings in Toronto?",
        a: "Yes — Toronto and the GTA is one of our most active international markets, especially for Sikh and Hindu Indo-Canadian weddings.",
      },
      {
        q: "Can the invite include Toronto venue illustrations?",
        a: "Absolutely — venue illustrations of GTA hotels, country estates and gurudwaras are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Toronto?",
        a: "Yes — Stationery Wala ships to Toronto via DHL Express, typically 7-10 business days.",
      },
      {
        q: "Can you typeset Gurmukhi for Toronto Sikh weddings?",
        a: "Yes — Sikh wedding invitations with Ek Onkar, Gurmukhi and traditional Anand Karaj conventions are one of our most-loved Toronto formats.",
      },
      {
        q: "Do you work on Toronto time zones?",
        a: "Yes — our team takes WhatsApp calls on EST and delivers invites that align with Toronto wedding schedules.",
      },
      {
        q: "Can you design for inter-cultural Indo-Canadian weddings?",
        a: "Yes — inter-cultural weddings (Indian + Canadian, Indian + Chinese-Canadian, Indian + Caribbean) are a growing part of our Toronto portfolio.",
      },
    ],
  },
  {
    slug: "vancouver",
    city: "Vancouver",
    country: "CA",
    region: "Canada",
    type: "international",
    metaTitle:
      "Wedding Invitations Vancouver | Indo-Canadian E-Invites | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Vancouver — Sikh Anand Karaj ceremonies, Indo-Canadian weddings, custom designed. WhatsApp delivered.",
    heroImage: HERO_DEFAULT,
    intro:
      "Vancouver and Surrey host one of Canada's largest Sikh and South Asian communities — and Vancouver Indian weddings carry significant Punjabi heritage and ceremony. From Sikh Anand Karaj ceremonies at the many gurudwaras across Surrey and Abbotsford, to grand receptions at Vancouver hotel ballrooms, our wedding invitations honour Vancouver's distinctly Indo-Canadian wedding culture.",
    sections: [
      {
        h2: "Vancouver Indo-Canadian aesthetics",
        paragraphs: [
          "Vancouver Indian weddings have a strong Punjabi-Sikh foundation, often combined with British Columbia's natural beauty as backdrop — coastal venues, mountain estates, and the iconic gurudwaras across Surrey. Our wedding invitations honour this combination through appropriate typography, motifs and venue illustrations.",
        ],
        bullets: [
          "Punjabi/Gurmukhi + English bilingual typesetting",
          "Sikh Anand Karaj ceremony format",
          "Custom illustration of BC landscape or your venue",
          "Multi-venue Vancouver invite handling",
          "Ship printed stationery to Vancouver via DHL",
        ],
      },
      {
        h2: "Vancouver/BC venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across Vancouver, Surrey, Abbotsford, Burnaby and the lower mainland. Common venues include the Fairmont Pacific Rim, Vancouver Convention Centre, Rosewood Hotel Georgia, Sutton Place, the many gurudwaras across Surrey, and BC estate venues.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design for Sikh weddings in Vancouver?",
        a: "Yes — Vancouver and Surrey have one of Canada's largest Sikh communities, and Sikh wedding invitations are one of our most-loved Vancouver formats.",
      },
      {
        q: "Can the invite include Vancouver or BC landscape illustrations?",
        a: "Absolutely — BC coastal scenes, mountain backdrops and venue illustrations are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Vancouver?",
        a: "Yes — Stationery Wala ships to Vancouver via DHL Express, typically 7-10 business days.",
      },
      {
        q: "Can you typeset Gurmukhi correctly?",
        a: "Yes — proper Gurmukhi typography with traditional Sikh ceremonial conventions.",
      },
      {
        q: "Do you work on PST time zones?",
        a: "Yes — our team takes WhatsApp calls on PST and delivers invites that align with Vancouver wedding schedules.",
      },
      {
        q: "Can you design for Punjabi-Hindu inter-community weddings?",
        a: "Yes — inter-community weddings within the Punjabi diaspora are common in Vancouver, and we design with appropriate sensitivity to both family traditions.",
      },
    ],
  },
  {
    slug: "sydney",
    city: "Sydney",
    country: "AU",
    region: "Australia",
    type: "international",
    metaTitle:
      "Wedding Invitations Sydney | Indian E-Invites Australia | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Sydney — custom designed for Indo-Australian weddings. WhatsApp delivered. Ship stationery to Sydney.",
    heroImage: HERO_DEFAULT,
    intro:
      "Sydney Indian weddings combine Indian cultural depth with Australia's signature coastal elegance. From temple ceremonies in Westmead and Parramatta to grand receptions at iconic Sydney harbour-side venues, our wedding invitations honour Sydney's distinctly Indo-Australian wedding culture with appropriate sensitivity and international polish.",
    sections: [
      {
        h2: "Sydney Indian wedding aesthetics",
        paragraphs: [
          "Sydney Indian weddings often happen across multiple venues and span the harbour, beach and inner-city landscape. Our wedding invitations are designed to honour both the Indian ceremonial heritage and Sydney's signature coastal aesthetic.",
        ],
        bullets: [
          "Hindi/Gujarati/Tamil + English bilingual typesetting",
          "Custom illustration of Sydney harbour or your venue",
          "Multi-venue Sydney invite handling",
          "Ship printed stationery to Sydney via DHL",
          "WhatsApp delivery on AEST time zones",
        ],
      },
      {
        h2: "Sydney venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across Sydney — Doltone House, The Calyx Royal Botanic Garden, Star Sydney, Sofitel Wentworth, Four Seasons, the many Hindu and Sikh temples across Westmead, Helensburgh, Parramatta, and harbour-side venues for receptions.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for Sydney?",
        a: "Yes — Sydney is one of our most active Australian markets. We design for Indo-Australian, Sikh, Hindu and inter-cultural weddings.",
      },
      {
        q: "Can the invite include Sydney landmark illustrations?",
        a: "Absolutely — Sydney Opera House, Harbour Bridge and venue illustrations are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Sydney?",
        a: "Yes — Stationery Wala ships to Sydney via DHL Express, typically 7-10 business days.",
      },
      {
        q: "Do you work on Sydney time zones?",
        a: "Yes — our team takes WhatsApp calls on AEST and delivers invites that align with Sydney wedding schedules.",
      },
      {
        q: "Can you design for inter-cultural Indo-Australian weddings?",
        a: "Yes — inter-cultural weddings are a growing part of our Sydney portfolio.",
      },
      {
        q: "How fast is delivery for a Sydney wedding invitation?",
        a: "Digital delivery is 24 hours from final brief.",
      },
    ],
  },
  {
    slug: "melbourne",
    city: "Melbourne",
    country: "AU",
    region: "Australia",
    type: "international",
    metaTitle:
      "Wedding Invitations Melbourne | Indo-Australian E-Invites | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Melbourne — custom designed for Indo-Australian weddings. WhatsApp delivered. Ship stationery to Melbourne.",
    heroImage: HERO_DEFAULT,
    intro:
      "Melbourne Indian weddings carry the city's signature design-aware elegance — couples here often value craft, restraint and considered typography in their wedding invitations. At InvitationWala we design wedding invitations in Melbourne with the design polish the city is known for.",
    sections: [
      {
        h2: "Melbourne Indian wedding aesthetics",
        paragraphs: [
          "Melbourne is one of the most design-aware cities in the southern hemisphere, and Indian weddings here often combine traditional Indian ceremonies with Melbourne's signature design polish. Our wedding invitations honour both.",
        ],
        bullets: [
          "Hindi/Gujarati/Tamil + English bilingual typesetting",
          "Custom illustration of Melbourne landmarks or your venue",
          "Multi-venue Melbourne invite handling",
          "Ship printed stationery to Melbourne via DHL",
          "WhatsApp delivery on AEDT time zones",
        ],
      },
      {
        h2: "Melbourne venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across Melbourne — Crown Casino, Plaza Ballroom, Park Hyatt, Sofitel, Grand Hyatt, the many Hindu temples across Carrum Downs and Sunshine, and country estate venues across Victoria.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for Melbourne?",
        a: "Yes — Melbourne is part of our active Australian market.",
      },
      {
        q: "Can the invite include Melbourne landmark illustrations?",
        a: "Yes — Melbourne city landmarks and venue illustrations are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Melbourne?",
        a: "Yes — Stationery Wala ships to Melbourne via DHL Express, typically 7-10 business days.",
      },
      {
        q: "Do you work on Melbourne time zones?",
        a: "Yes — our team takes WhatsApp calls on AEDT.",
      },
      {
        q: "Can you handle Victorian estate destination wedding invites?",
        a: "Yes — Victorian estate destination weddings are increasingly popular and we design full destination wedding suites for them.",
      },
      {
        q: "How fast is delivery for a Melbourne wedding invitation?",
        a: "Digital delivery is 24 hours from final brief.",
      },
    ],
  },
  {
    slug: "singapore",
    city: "Singapore",
    country: "SG",
    region: "Singapore",
    type: "international",
    metaTitle:
      "Wedding Invitations Singapore | Indian E-Invites Singapore | InvitationWala",
    metaDescription:
      "Premium Indian wedding invitations in Singapore — custom designed for Indian and Indo-Singaporean weddings. WhatsApp delivered. Ship to Singapore.",
    heroImage: HERO_DEFAULT,
    intro:
      "Singapore hosts a sophisticated Indian community — and Singapore Indian weddings carry the production polish the city is known for. From Hindu temple ceremonies at Sri Mariamman to grand hotel receptions at Marina Bay Sands and Capella, our wedding invitations match Singapore's standards with appropriate craft and design polish.",
    sections: [
      {
        h2: "Singapore Indian wedding aesthetics",
        paragraphs: [
          "Singapore Indian weddings often blend Tamil, Telugu, North Indian and inter-cultural traditions with Singapore's signature design polish. Our wedding invitations honour both with appropriate cultural sensitivity and international production value.",
        ],
        bullets: [
          "Tamil/Hindi/Gujarati + English bilingual typesetting",
          "Custom illustration of Marina Bay or your venue",
          "Multi-venue Singapore invite handling",
          "Ship printed stationery to Singapore via DHL",
          "WhatsApp delivery on SGT time zones",
        ],
      },
      {
        h2: "Singapore venues we design for",
        paragraphs: [
          "We design wedding invitations for Indian weddings at venues across Singapore — Marina Bay Sands, Capella Singapore, Raffles Hotel, Fullerton Bay, Shangri-La, the many Hindu temples in Little India, and private island destination venues in Sentosa.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do you design Indian wedding invitations for Singapore?",
        a: "Yes — Singapore is part of our active international market, especially for Tamil, Telugu and North Indian weddings.",
      },
      {
        q: "Can the invite include Singapore landmark illustrations?",
        a: "Yes — Marina Bay Sands, ArtScience Museum and venue illustrations are part of our standard offering.",
      },
      {
        q: "Do you ship printed stationery to Singapore?",
        a: "Yes — Stationery Wala ships to Singapore via DHL Express, typically 5-7 business days.",
      },
      {
        q: "Can you typeset Tamil for Singapore Tamil weddings?",
        a: "Yes — proper Tamil typography with traditional Tamil-Brahmin or Tamil-community conventions.",
      },
      {
        q: "Do you work on Singapore time zones?",
        a: "Yes — our team takes WhatsApp calls on SGT.",
      },
      {
        q: "How fast is delivery for a Singapore wedding invitation?",
        a: "Digital delivery is 24 hours from final brief.",
      },
    ],
  },
];

// Lookup helpers
export const getCityBySlug = (slug) => CITIES.find((c) => c.slug === slug);
export const indianCities = () => CITIES.filter((c) => c.type === "india");
export const internationalCities = () =>
  CITIES.filter((c) => c.type === "international");
